import { useState, useEffect, useCallback, useRef } from 'react';
import attendanceService, { AttendanceSession } from '../services/attendanceService';
import { getCurrentBrowserTime } from '../utils/dateUtils';
import { useAuth } from '../contexts/AuthContext';

// function getGPS(): Promise<{ latitude: number; longitude: number } | null> {
//     return new Promise((resolve) => {
//         if (!navigator.geolocation) return resolve(null);
//         navigator.geolocation.getCurrentPosition(
//             (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
//             () => resolve(null),
//             { timeout: 5000 }
//         );
//     });
// }

/**
 * Convert raw API/network errors into clean user-facing messages.
 * Prevents Spring Boot internals (e.g. "No static resource ...") from showing in the UI.
 */
function getFriendlyError(err: any, fallback: string): string {
    const status: number | undefined = err?.response?.status;
    const serverMsg: string | undefined = err?.response?.data?.message;

    // 404 = endpoint not implemented on backend yet
    if (status === 404 || (serverMsg && serverMsg.toLowerCase().includes('no static resource'))) {
        return 'Attendance API is not yet configured on the server. Please contact your administrator.';
    }
    // 409 = already checked in / already completed
    if (status === 409) return serverMsg || 'Conflicting session state. Please refresh and try again.';
    // 401/403
    if (status === 401 || status === 403) return 'You are not authorised to perform this action.';
    // Network error (no response)
    if (!err?.response) return 'Network error. Please check your connection.';
    // Use server message if clean, otherwise fallback
    return serverMsg || fallback;
}

export function useAttendance(facilityId: string) {
    const { user } = useAuth();
    const [session, setSession] = useState<AttendanceSession | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isActionLoading, setIsActionLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [elapsed, setElapsed] = useState(0); // seconds since check-in
    const timerRef = useRef<NodeJS.Timeout | null>(null);
    const autoCheckoutAttempted = useRef(false);

    const caregiverId = user?.id || null;

    const startTimer = useCallback((checkInTime: string | null | undefined) => {
        if (!checkInTime) return;
        if (timerRef.current) clearInterval(timerRef.current);
        const tickFn = () => {
            const now = new Date();
            // Parse as local time (backend sends LocalDateTime strings without Z)
            const localCheckIn = new Date(checkInTime);
            const diff = Math.floor((now.getTime() - localCheckIn.getTime()) / 1000);

            // Ensure no negative elapsed time
            setElapsed(Math.max(0, diff));
        };
        tickFn();
        timerRef.current = setInterval(tickFn, 1000);
    }, []);

    const stopTimer = useCallback(() => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
        setElapsed(0);
    }, []);

    // Fetch active session on mount
    useEffect(() => {
        if (!caregiverId) {
            setIsLoading(false);
            return;
        }
        setIsLoading(true);
        // Check localStorage as fallback
        const cachedSessionRaw = localStorage.getItem('activeAttendanceSession');
        let cachedSession: AttendanceSession | null = null;
        if (cachedSessionRaw) {
            try {
                const parsed = JSON.parse(cachedSessionRaw);
                // Only use cache if it was created today
                if (parsed.checkIn && new Date(parsed.checkIn).toDateString() === new Date().toDateString()) {
                    cachedSession = parsed;
                } else {
                    localStorage.removeItem('activeAttendanceSession');
                }
            } catch (e) { }
        }

        // attendanceService.getActiveSession(caregiverId)
        //     .then((activeSession) => {
        //         const sessionToUse = activeSession || cachedSession;
        //         setSession(sessionToUse);
        //         if (sessionToUse?.status === 'CHECKED_IN') {
        //             startTimer(sessionToUse.checkIn);
        //         }
        //     })
        //     .catch(() => {
        //         // Silently fail if API not ready or throws 500, fallback to cache
        //         if (cachedSession) {
        //             setSession(cachedSession);
        //             if (cachedSession.status === 'CHECKED_IN') {
        //                 startTimer(cachedSession.checkIn);
        //             }
        //         }
        //         setError(null);
        //     })
        //     .finally(() => setIsLoading(false));

        // Use cached session directly to avoid the 500 error from the active session API
        if (cachedSession) {
            setSession(cachedSession);
            if (cachedSession.status === 'CHECKED_IN') {
                startTimer(cachedSession.checkIn);
            }
        }
        setIsLoading(false);

        return () => stopTimer();
    }, [caregiverId, startTimer, stopTimer]);

    const handleCheckIn = useCallback(async () => {
        if (!caregiverId || !facilityId) return;
        setIsActionLoading(true);
        setError(null);
        try {
            // Use local browser time instead of UTC ISO string
            const loginTime = getCurrentBrowserTime();
            const newSession = await attendanceService.autoCheckIn(facilityId, loginTime);
            localStorage.setItem('activeAttendanceSession', JSON.stringify(newSession));
            setSession(newSession);
            startTimer(newSession.checkIn);
        } catch (err: any) {
            setError(getFriendlyError(err, 'Check-in failed. Please try again.'));
        } finally {
            setIsActionLoading(false);
        }
    }, [caregiverId, facilityId, startTimer]);

    const handleCheckOut = useCallback(async () => {
        if (!caregiverId || !facilityId || !session) return;
        setIsActionLoading(true);
        setError(null);
        try {
            // Use the new autoCheckOut API
            const dateStr = session.date || (session.checkIn ? session.checkIn.slice(0, 10) : new Date().toISOString().slice(0, 10));
            const logoutTime = getCurrentBrowserTime();
            const updatedSession = await attendanceService.autoCheckOut(
                facilityId,
                { caregiverId: session.caregiverId || caregiverId, date: dateStr, logoutTime, checkoutTime: logoutTime, checkOutTime: logoutTime, checkOut: logoutTime }
            );
            localStorage.setItem('activeAttendanceSession', JSON.stringify(updatedSession));
            setSession(updatedSession);
            stopTimer();
        } catch (err: any) {
            setError(getFriendlyError(err, 'Check-out failed. Please try again.'));
        } finally {
            setIsActionLoading(false);
        }
    }, [caregiverId, facilityId, session, stopTimer]);

    // Auto-checkout when shift is complete
    useEffect(() => {
        if (session?.status === 'CHECKED_IN' && session?.shiftHours && elapsed > 0) {
            const maxSeconds = session.shiftHours * 3600;
            if (elapsed >= maxSeconds && !isActionLoading && !autoCheckoutAttempted.current) {
                // Time to auto-checkout since they forgot
                autoCheckoutAttempted.current = true;
                handleCheckOut();
            }
        }
    }, [elapsed, session, handleCheckOut, isActionLoading]);

    return {
        session,
        isLoading,
        isActionLoading,
        error,
        elapsed,
        caregiverId,
        handleCheckIn,
        handleCheckOut,
    };
}

export default useAttendance;
